import React from 'react';
import PropTypes from 'prop-types';

import { useI18n } from '@hydra/common/customHooks';
import { Translate } from '@hydra/common/components';
import { Criterium, CriteriumSkeleton } from '@hydra/common/criterium';
import { useReviewContext } from '@hydra/common/context';

const generateLanguageObject = languageString => {
  if (!languageString) {
    return null;
  }

  const lowerStr = languageString.toLowerCase().trim();
  const langsCodesRegex = /^([a-z]{2,3})(-([a-z]{2,5}))?$/g;
  const matches = langsCodesRegex.exec(lowerStr);

  if (matches === null) {
    return null;
  } else {
    return {
      lang: matches[1],
      country: matches[2] ? matches[3] : null
    };
  }
};

const getLanguageTag = ({ detectedLanguage }) => {
  return detectedLanguage ? `lang_${detectedLanguage.lang}` : null;
};

export const Language = ({ criterium }) => {
  const { translate } = useI18n();
  const { data } = criterium;
  const { variant } = useReviewContext();

  const detectedLanguage = generateLanguageObject(data.detected);
  const declaredLanguage = generateLanguageObject(data.declared);

  const translateLanguageCode = (code) => {
    if (!code || !code.lang) {
      return null;
    }

    const translatedLanguage = translate(`lang_${code.lang}`);

    const translatedCountry = (code.country)
      ? translate(`country_name_${code.country.toUpperCase()}`)
      : null;

    return translatedCountry
      ? `${translatedLanguage} (${translatedCountry})`
      : translatedLanguage;
  };

  const content = (
    <>
      <p>
        {
          variant === 'nocrit'
            ? <CriteriumSkeleton width='30%' />
            : (
              <>
                <Translate content='review_language_declared' component='strong' />
                {
                  declaredLanguage
                    ? <i>{translateLanguageCode(declaredLanguage)}</i>
                    : <Translate content='review_missing' />
                }
              </>
              )
        }
      </p>
      <p>
        {
          variant === 'nocrit'
            ? <CriteriumSkeleton width='30%' />
            : (
              <>
                <Translate content='review_language_detected' component='strong' />
                {
                  detectedLanguage
                    ? <i>{translateLanguageCode(detectedLanguage)}</i>
                    : <Translate content='review_missing' />
                }
              </>
              )
        }
      </p>
    </>
  );

  const languageTag = getLanguageTag({ detectedLanguage });
  const translatedTag = languageTag ? translate(languageTag) : '';

  return (
    <Criterium
      criterium={criterium}
      variables={{ detectedLanguage: translatedTag }}
    >
      {content}
    </Criterium>
  );
};

Language.propTypes = {
  criterium: PropTypes.shape({
    data: PropTypes.shape({
      detected: PropTypes.string,
      declared: PropTypes.string
    })
  })
};

export default React.memo(Language);
